import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  useCampaigns,
  useCreateCampaign,
  useCreatives,
  useDeleteCampaign,
  usePatchCampaign,
  usePublishers,
  useSegments,
} from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { CampaignCreate, CampaignOut, CampaignPatch, CampaignStatus } from '@/api/types';
import { CAMPAIGN_STATUSES } from '@/api/types';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { Checkbox } from '@/components/arc/checkbox/checkbox';
import { Dialog, DialogContent } from '@/components/arc/dialog/dialog';
import { Drawer, DrawerContent } from '@/components/arc/drawer/drawer';
import { DropdownMenu } from '@/components/arc/dropdown-menu/dropdown-menu';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { Input } from '@/components/arc/input/input';
import { MultiSelect } from '@/components/arc/multi-select/multi-select';
import { NumberField } from '@/components/arc/number-field/number-field';
import SegmentedControl from '@/components/arc/segmented-control/segmented-control';
import { Select } from '@/components/arc/select/select';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { useToastStack } from '@/components/arc/toast-stack/toast-stack';
import { CampaignStatusBadge } from '@/components/status/StatusBadge';
import { fmtDate, fmtMoney, fromLocalInput, labelize, toLocalInput } from '@/lib/format';
import { useHref } from '@/module/basePath';
import { useTimezone } from '@/module/MomentProvider';
import styles from './overlay.module.css';

type CampaignRow = { [K in keyof CampaignOut]: CampaignOut[K] } & {
  segment_name: string;
  creative_name: string;
  publisher_count: number;
};
type StatusFilter = 'all' | CampaignStatus;
type Confirm = { campaign: CampaignOut; action: 'end' | 'delete' };

export function CampaignsPage() {
  const tz = useTimezone();
  const href = useHref();
  const navigate = useNavigate();
  const { toast } = useToastStack();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const list = useCampaigns(statusFilter === 'all' ? undefined : statusFilter);
  const segs = useSegments();
  const creatives = useCreatives();
  const patch = usePatchCampaign();
  const del = useDeleteCampaign();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<CampaignOut | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const segmentNames = new Map((segs.data?.pages.flatMap((p) => p.items) ?? []).map((s) => [s.id, s.name]));
  const creativeNames = new Map((creatives.data?.items ?? []).map((c) => [c.id, c.name]));
  const rows: CampaignRow[] = (list.data?.items ?? []).map((c) => ({
    ...c,
    segment_name: segmentNames.get(c.segment_id) ?? c.segment_id,
    creative_name: creativeNames.get(c.creative_id) ?? c.creative_id,
    publisher_count: c.publisher_ids.length,
  }));
  const isEmpty = !list.isLoading && !list.isError && rows.length === 0;

  const openEditor = (campaign: CampaignOut | null) => {
    setEditing(campaign);
    setDrawerOpen(true);
  };
  const setStatus = (c: CampaignOut, status: CampaignStatus) =>
    patch.mutateAsync({ id: c.id, body: { status } }).catch((e: unknown) => {
      toast({
        type: 'error',
        title: `Could not ${status === 'paused' ? 'pause' : status === 'active' ? 'activate' : 'end'} ${c.name}`,
        description: errorMessage(e),
      });
    });
  const ask = (campaign: CampaignOut, action: Confirm['action']) => {
    setConfirm({ campaign, action });
    setConfirmOpen(true);
  };

  const columns: DataColumn<CampaignRow>[] = [
    {
      key: 'name',
      label: 'Campaign',
      width: 180,
      render: (_, c) => (
        <div className={styles.cell}>
          <span className={styles.cellMain} title={c.name}>
            {c.name}
          </span>
          <span className={styles.cellSub}>{c.advertiser_name ?? 'No advertiser'}</span>
        </div>
      ),
    },
    { key: 'status', label: 'Status', width: 92, render: (_, c) => <CampaignStatusBadge status={c.status} /> },
    {
      key: 'segment_name',
      label: 'Segment',
      render: (_, c) => (
        <div className={styles.cell}>
          <span className={styles.cellMain} title={c.segment_name}>
            {c.segment_name}
          </span>
          <span className={styles.cellSub} title={c.creative_name}>
            {c.creative_name}
          </span>
        </div>
      ),
    },
    { key: 'publisher_count', label: 'Publishers', numeric: true, width: 100 },
    { key: 'cpm_usd', label: 'CPM', numeric: true, width: 80, render: (v) => fmtMoney(Number(v), 2) },
    {
      key: 'start_at',
      label: 'Flight',
      width: 136,
      render: (_, c) => (
        <span className={`${styles.cell} text-secondary`}>
          <span>{c.start_at ? fmtDate(c.start_at, tz) : 'Starts now'}</span>
          <span className={styles.cellSub}>{c.end_at ? `to ${fmtDate(c.end_at, tz)}` : 'Open end'}</span>
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      width: 232,
      render: (_, c) => {
        const busy = patch.isPending && patch.variables?.id === c.id;
        return (
          <span className={styles.rowActions}>
            {c.status === 'active' ? (
              <Button variant="ghost" size="sm" loading={busy} onClick={() => void setStatus(c, 'paused')}>
                Pause
              </Button>
            ) : c.status !== 'ended' ? (
              <Button variant="ghost" size="sm" loading={busy} onClick={() => void setStatus(c, 'active')}>
                Activate
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(href(`/overlay/campaigns/${c.id}/stats`))}
            >
              Stats
            </Button>
            <DropdownMenu
              label="More"
              items={[
                { label: 'Edit campaign', onSelect: () => openEditor(c) },
                { label: 'End campaign', disabled: c.status === 'ended', onSelect: () => ask(c, 'end') },
                {
                  label: 'Delete campaign',
                  destructive: true,
                  separatorBefore: true,
                  onSelect: () => ask(c, 'delete'),
                },
              ]}
            />
          </span>
        );
      },
    },
  ];

  return (
    <>
      <section className="stack-4" aria-labelledby="campaigns-heading">
        <div className={styles.sectionHead}>
          <div className={styles.cell}>
            <h2 id="campaigns-heading" className={styles.sectionTitle}>
              Campaigns
            </h2>
            <span className={styles.sectionMeta}>
              A campaign shows a creative on the moments of a segment across selected publishers
            </span>
          </div>
          <div className={styles.headActions}>
            <SegmentedControl
              label="Status filter"
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as StatusFilter)}
              options={[
                { value: 'all', label: 'All' },
                ...CAMPAIGN_STATUSES.map((s) => ({ value: s, label: labelize(s) })),
              ]}
            />
            {!isEmpty && (
              <Button onClick={() => openEditor(null)}>
                <Plus size={16} strokeWidth={1.75} aria-hidden="true" /> New campaign
              </Button>
            )}
          </div>
        </div>

        {list.isLoading ? (
          <Skeleton lines={5} label="Loading campaigns" />
        ) : list.isError ? (
          <Alert tone="danger" title="Campaigns did not load">
            {errorMessage(list.error)}{' '}
            <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : isEmpty ? (
          <EmptyState
            title={
              statusFilter === 'all'
                ? 'No campaigns yet'
                : `No ${labelize(statusFilter).toLowerCase()} campaigns`
            }
            description={
              statusFilter === 'all'
                ? 'Pair a segment with a creative and pick the publishers it runs on.'
                : 'Campaigns with another status are hidden by the filter.'
            }
            action={
              statusFilter === 'all' ? (
                <Button onClick={() => openEditor(null)}>
                  <Plus size={16} strokeWidth={1.75} aria-hidden="true" /> New campaign
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => setStatusFilter('all')}>
                  Show all campaigns
                </Button>
              )
            }
          />
        ) : (
          <div className="table-scroll">
            <SortableDataTable<CampaignRow>
              rows={rows}
              columns={columns}
              rowKey="id"
              caption="Campaigns"
              defaultSort={{ key: 'name', direction: 'asc' }}
              itemName={{ one: 'campaign', other: 'campaigns' }}
            />
          </div>
        )}
      </section>

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent
          title={editing ? 'Edit campaign' : 'New campaign'}
          description={
            editing
              ? 'Segment and creative are fixed once a campaign exists.'
              : 'Manifests rebuild when the campaign is saved.'
          }
          className={styles.drawerWide}
        >
          <CampaignForm key={editing?.id ?? 'new'} campaign={editing} onDone={() => setDrawerOpen(false)} />
        </DrawerContent>
      </Drawer>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          title={
            confirm?.action === 'delete'
              ? `Delete ${confirm.campaign.name}?`
              : `End ${confirm?.campaign.name ?? 'campaign'}?`
          }
          description={
            confirm?.action === 'delete'
              ? 'The campaign and its stats are removed. Publishers stop showing it on the next manifest rebuild.'
              : 'An ended campaign stops serving and cannot be activated again.'
          }
        >
          <div className={styles.footer}>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={confirm?.action === 'delete' ? 'danger' : 'primary'}
              loading={confirm?.action === 'delete' ? del.isPending : patch.isPending}
              onClick={async () => {
                if (!confirm) return;
                try {
                  if (confirm.action === 'delete') await del.mutateAsync(confirm.campaign.id);
                  else await patch.mutateAsync({ id: confirm.campaign.id, body: { status: 'ended' } });
                  setConfirmOpen(false);
                } catch (e) {
                  toast({
                    type: 'error',
                    title:
                      confirm.action === 'delete'
                        ? 'Could not delete the campaign'
                        : 'Could not end the campaign',
                    description: errorMessage(e),
                  });
                }
              }}
            >
              {confirm?.action === 'delete' ? 'Delete campaign' : 'End campaign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CampaignForm({ campaign, onDone }: { campaign: CampaignOut | null; onDone: () => void }) {
  const { toast } = useToastStack();
  const segs = useSegments();
  const creatives = useCreatives();
  const pubs = usePublishers();
  const create = useCreateCampaign();
  const patch = usePatchCampaign();

  const [name, setName] = useState(campaign?.name ?? '');
  const [advertiser, setAdvertiser] = useState(campaign?.advertiser_name ?? '');
  const [segmentId, setSegmentId] = useState(campaign?.segment_id ?? '');
  const [creativeId, setCreativeId] = useState(campaign?.creative_id ?? '');
  const [publisherIds, setPublisherIds] = useState<string[]>(campaign?.publisher_ids ?? []);
  const [startAt, setStartAt] = useState(toLocalInput(campaign?.start_at));
  const [endAt, setEndAt] = useState(toLocalInput(campaign?.end_at));
  const [cpm, setCpm] = useState(campaign?.cpm_usd ?? 5);
  const [budget, setBudget] = useState(campaign?.budget_usd ?? 0);
  const [perUserPerDay, setPerUserPerDay] = useState(campaign?.frequency_cap.per_user_per_day ?? 3);
  const [perVideo, setPerVideo] = useState(campaign?.frequency_cap.per_video ?? 1);
  const [minScore, setMinScore] = useState(campaign?.trigger.min_moment_score ?? 0);
  const [delay, setDelay] = useState(campaign?.trigger.delay_sec ?? 0);
  const [onlyFirst, setOnlyFirst] = useState(campaign?.trigger.only_first_match ?? false);
  const [status, setStatus] = useState<CampaignStatus>(campaign?.status ?? 'draft');

  const segmentOptions = (segs.data?.pages.flatMap((p) => p.items) ?? []).map((s) => ({
    value: s.id,
    label: `${s.name} (${s.granularity}, ${s.video_count} videos)`,
  }));
  const creativeOptions = (creatives.data?.items ?? []).map((c) => ({
    value: c.id,
    label: `${c.name} (${labelize(c.format)})`,
  }));
  const publisherOptions = (pubs.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }));

  const pending = create.isPending || patch.isPending;
  const error = create.error ?? patch.error;
  const valid = name.trim() !== '' && segmentId !== '' && creativeId !== '';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || pending) return;
    const shared: CampaignPatch = {
      name: name.trim(),
      advertiser_name: advertiser.trim() || null,
      publisher_ids: publisherIds,
      start_at: fromLocalInput(startAt),
      end_at: fromLocalInput(endAt),
      frequency_cap: { per_user_per_day: perUserPerDay, per_video: perVideo },
      trigger: { min_moment_score: minScore, delay_sec: delay, only_first_match: onlyFirst },
      budget_usd: budget > 0 ? budget : null,
      cpm_usd: cpm,
      status,
    };
    try {
      if (campaign) {
        await patch.mutateAsync({ id: campaign.id, body: shared });
      } else {
        const body: CampaignCreate = {
          ...shared,
          name: name.trim(),
          segment_id: segmentId,
          creative_id: creativeId,
        };
        await create.mutateAsync(body);
      }
      toast({
        type: 'success',
        title: 'Campaign saved',
        description: 'Overlay manifests are rebuilding in the background.',
      });
      onDone();
    } catch {
      /* shown in the alert below */
    }
  };

  return (
    <form className={styles.form} onSubmit={(e) => void submit(e)}>
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
      <Input
        label="Advertiser"
        value={advertiser}
        onChange={(e) => setAdvertiser(e.target.value)}
        placeholder="Optional"
      />
      <Select
        label="Segment"
        value={segmentId}
        onValueChange={setSegmentId}
        disabled={!!campaign || segmentOptions.length === 0}
        placeholder={
          segs.isLoading ? 'Loading segments' : segmentOptions.length ? 'Select a segment' : 'No segments yet'
        }
        options={segmentOptions}
      />
      <Select
        label="Creative"
        value={creativeId}
        onValueChange={setCreativeId}
        disabled={!!campaign || creativeOptions.length === 0}
        placeholder={
          creatives.isLoading
            ? 'Loading creatives'
            : creativeOptions.length
              ? 'Select a creative'
              : 'No creatives yet'
        }
        options={creativeOptions}
      />
      <MultiSelect
        label="Publishers"
        options={publisherOptions}
        value={publisherIds}
        onValueChange={setPublisherIds}
        placeholder={publisherOptions.length ? 'Select publishers' : 'No publishers yet'}
        disabled={publisherOptions.length === 0}
        description="Leave empty to run on every publisher"
        maxVisible={3}
      />

      <div className={styles.formSection}>
        <span className={styles.subTitle}>Schedule and pricing</span>
        <div className={styles.formGrid2}>
          <Input
            label="Start"
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
            description="Empty starts now"
          />
          <Input
            label="End"
            type="datetime-local"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            description="Empty keeps it open"
          />
          <NumberField label="CPM" value={cpm} onValueChange={setCpm} min={0} step={0.01} prefix="$" />
          <NumberField
            label="Budget"
            value={budget}
            onValueChange={setBudget}
            min={0}
            step={1}
            prefix="$"
            description="0 means no budget cap"
          />
        </div>
      </div>

      <div className={styles.formSection}>
        <span className={styles.subTitle}>Frequency and trigger</span>
        <div className={styles.formGrid2}>
          <NumberField
            label="Cap per user per day"
            value={perUserPerDay}
            onValueChange={setPerUserPerDay}
            min={0}
          />
          <NumberField label="Cap per video" value={perVideo} onValueChange={setPerVideo} min={0} />
          <NumberField
            label="Minimum moment score"
            value={minScore}
            onValueChange={setMinScore}
            min={0}
            max={1}
            step={0.05}
          />
          <NumberField
            label="Delay"
            value={delay}
            onValueChange={setDelay}
            min={0}
            suffix=" s"
            description="After the moment starts"
          />
        </div>
        <Checkbox
          label="Only first match per video"
          description="Show the overlay once per video, on the first matching moment"
          checked={onlyFirst}
          onCheckedChange={(v) => setOnlyFirst(v === true)}
        />
      </div>

      <div className={styles.formSection}>
        <Select
          label="Status"
          value={status}
          onValueChange={(v) => setStatus(v as CampaignStatus)}
          options={CAMPAIGN_STATUSES.map((s) => ({ value: s, label: labelize(s) }))}
        />
      </div>

      {error && (
        <Alert tone="danger" title="Could not save the campaign">
          {errorMessage(error)}
        </Alert>
      )}
      <div className={styles.footer}>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" loading={pending} disabled={!valid}>
          {campaign ? 'Save campaign' : 'Create campaign'}
        </Button>
      </div>
    </form>
  );
}
